import React, { useEffect, useState } from 'react';
import campaignApi from '../../api/campaigns.api';
import { Contact } from '../../types/campaign.types';
import { useToast } from '../../contexts/ToastContext';
import Button from '../common/Button';
import Input from '../common/Input';
import LoadingSpinner from '../common/LoadingSpinner';

interface AddContactFormProps {
    campaignId: number;
    onSuccess: () => void;
    onCancel: () => void;
    contact?: Contact;
}

const getInitialFormData = (contact?: Contact) => ({
    name: contact?.name || '',
    phone_number: contact?.phone_number || '',
    email: contact?.email || '',
    scheduled_time: contact?.scheduled_time?.join(', ') || '',
    custom_fields: (contact?.custom_fields || {}) as Record<string, string>,
});

const AddContactForm: React.FC<AddContactFormProps> = ({ campaignId, onSuccess, onCancel, contact }) => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const isEditing = Boolean(contact);

    const [formData, setFormData] = useState(getInitialFormData(contact));

    const [customFieldKey, setCustomFieldKey] = useState('');
    const [customFieldValue, setCustomFieldValue] = useState('');

    useEffect(() => {
        setFormData(getInitialFormData(contact));
    }, [contact]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleAddCustomField = () => {
        if (!customFieldKey.trim()) {
            showToast('Custom field key is required', 'error');
            return;
        }

        setFormData((prev) => ({
            ...prev,
            custom_fields: {
                ...prev.custom_fields,
                [customFieldKey]: customFieldValue,
            },
        }));

        setCustomFieldKey('');
        setCustomFieldValue('');
    };

    const handleRemoveCustomField = (key: string) => {
        setFormData((prev) => {
            const newFields = { ...prev.custom_fields };
            delete newFields[key];
            return {
                ...prev,
                custom_fields: newFields,
            };
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validation
        if (!formData.phone_number.trim()) {
            showToast('Phone number is required', 'error');
            return;
        }

        setLoading(true);

        try {
            const payload = {
                name: formData.name.trim() || undefined,
                phone_number: formData.phone_number.trim(),
                email: formData.email.trim() || undefined,
                scheduled_time: formData.scheduled_time.trim() || undefined,
                custom_fields: Object.keys(formData.custom_fields).length > 0 ? formData.custom_fields : undefined,
            };

            if (contact) {
                await campaignApi.updateContact(campaignId, contact.id, payload);
                showToast('Contact updated successfully!', 'success');
            } else {
                await campaignApi.addSingleContact(campaignId, payload);
                showToast('Contact added successfully!', 'success');
                setFormData(getInitialFormData());
            }

            onSuccess();
        } catch (err) {
            const message = err instanceof Error
                ? err.message
                : `Failed to ${isEditing ? 'update' : 'add'} contact`;
            showToast(message, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Phone Number - Required */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number <span className="text-red-600">*</span>
                    </label>
                    <Input
                        name="phone_number"
                        type="tel"
                        placeholder="e.g., +233201234567 or 0201234567"
                        value={formData.phone_number}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Supports international format (+233...) or local (024..., 050...)
                    </p>
                </div>

                {/* Name - Optional */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name <span className="text-gray-400">(optional)</span>
                    </label>
                    <Input
                        name="name"
                        type="text"
                        placeholder="e.g., John Doe"
                        value={formData.name}
                        onChange={handleInputChange}
                        disabled={loading}
                    />
                </div>

                {/* Email - Optional */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email <span className="text-gray-400">(optional)</span>
                    </label>
                    <Input
                        name="email"
                        type="email"
                        placeholder="e.g., john@example.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        disabled={loading}
                    />
                </div>

                {/* Scheduled Time - Optional */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Scheduled Time <span className="text-gray-400">(optional)</span>
                    </label>
                    <Input
                        name="scheduled_time"
                        type="text"
                        placeholder="e.g., 7am, 3:30pm, or 19:00"
                        value={formData.scheduled_time}
                        onChange={handleInputChange}
                        disabled={loading}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Supports: 7am, 3:30pm, 19:00, or multiple times: "7am and 3pm"
                    </p>
                </div>

                {/* Custom Fields */}
                <div className="border-t pt-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-4">
                        Custom Fields <span className="text-gray-400">(optional)</span>
                    </h3>

                    {Object.keys(formData.custom_fields).length > 0 && (
                        <div className="mb-4 space-y-2">
                            {Object.entries(formData.custom_fields).map(([key, value]) => (
                                <div
                                    key={key}
                                    className="flex items-center justify-between bg-gray-50 px-4 py-2 rounded-lg"
                                >
                                    <div className="text-sm">
                                        <span className="font-medium text-gray-700">{key}:</span>
                                        <span className="text-gray-600 ml-2">{value}</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveCustomField(key)}
                                        className="text-red-600 hover:text-red-700 font-medium"
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="space-y-3">
                        <Input
                            type="text"
                            placeholder="Field name (e.g., department, location)"
                            value={customFieldKey}
                            onChange={(e) => setCustomFieldKey(e.target.value)}
                            disabled={loading}
                        />
                        <Input
                            type="text"
                            placeholder="Field value"
                            value={customFieldValue}
                            onChange={(e) => setCustomFieldValue(e.target.value)}
                            disabled={loading}
                        />
                        <Button
                            type="button"
                            onClick={handleAddCustomField}
                            disabled={loading || !customFieldKey.trim()}
                            className="w-full bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400"
                        >
                            + Add Custom Field
                        </Button>
                    </div>
                </div>

                {/* Form Actions */}
                <div className="border-t pt-6 flex gap-4 justify-end">
                    <Button
                        type="button"
                        onClick={onCancel}
                        disabled={loading}
                        className="bg-gray-500 hover:bg-gray-600"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        disabled={loading}
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400"
                    >
                        {loading ? <LoadingSpinner /> : isEditing ? 'Save Changes' : 'Add Contact'}
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default AddContactForm;
